import { useAuth } from "@clerk/clerk-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

export type GateStatus = "ok" | "not_registered" | "deactivated";

export interface AppUser {
  email: string;
  role: "user" | "admin" | null;
  banned: boolean;
  status: GateStatus;
}

interface AuthState {
  appUser: AppUser | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const AuthCtx = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const [appUser, setAppUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!isSignedIn) {
      setAppUser(null);
      setLoading(false);
      return;
    }
    try {
      const token = await getToken();
      const res = await fetch("/api/auth/user", {
        headers: { Authorization: `Bearer ${token ?? ""}` },
      });
      if (!res.ok) throw new Error(`gate returned ${res.status}`);
      setAppUser((await res.json()) as AppUser);
      setError(null);
    } catch (e) {
      setAppUser(null);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [isSignedIn, getToken]);

  useEffect(() => {
    if (isLoaded) void refresh();
  }, [isLoaded, refresh]);

  // Poll ~every 60s so guest-list changes take effect quickly (per the pattern).
  useEffect(() => {
    if (!isSignedIn) return;
    const t = setInterval(() => void refresh(), 60_000);
    return () => clearInterval(t);
  }, [isSignedIn, refresh]);

  return (
    <AuthCtx.Provider value={{ appUser, loading, error, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAppAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAppAuth must be used within AuthProvider");
  return ctx;
}
